import { GitWorkspaceAction, GitWorkspaceResult } from "./git-workspace.ts";
import { z } from "zod";
import { AgentEvent } from "./agent-events.ts";
import { ProviderId, ProviderSelection } from "./providers.ts";
import { GitCopyKind, GitCopyScope } from "./git-copy.ts";
import { MAX_JOB_MCP_SERVERS, McpServerConfig } from "./mcp.ts";
import { ProtocolIncompatible, ProtocolReady } from "./protocol-version.ts";
import { DirectoryEntry, MAX_DIRECTORY_ENTRIES, PullRequestStatus } from "./api.ts";
import {
  AttachmentRefs,
  ChatEvent,
  ChatEventsCoverage,
  Chat,
  DiffStat,
  Job,
  JobStatus,
  Repo,
  RepoAuth,
  WorkerCapabilities,
  WorktreeScriptSet,
  WorkerInfo,
  WorkerStatus,
  Worktree,
  TurnActor,
  PullRequestInfo,
} from "./models.ts";

/**
 * WebSocket wire protocol. Two socket kinds:
 *  - Worker socket:  CLI daemon ⇄ WorkerHub DO   (`/api/ws/worker`, bearer token)
 *  - Client socket:  web/native ⇄ OrgHub DO      (`/api/ws/org/:orgId`, session cookie/token)
 * All frames are JSON, validated with these schemas on both ends.
 * Writes from clients (new messages, cancels) go over REST; sockets are for
 * subscription, replay and low-latency fan-out.
 */

// ---------------------------------------------------------------------------
// Job assignment payload (server → worker)
// ---------------------------------------------------------------------------

export const JobSpec = z.object({
  jobId: z.string(),
  chatId: z.string(),
  provider: ProviderSelection,
  prompt: z.string(),
  /** Workspace instruction selected by the API when this job is created. */
  workspacePrompt: z.string().optional(),
  /** Required human attribution for the first response in this runtime. */
  initialTurn: TurnActor,
  repo: z.object({
    id: z.string(),
    /** For auth "github-app" this is the plain https URL (no credentials);
     * the worker fetches short-lived credentials via the git-credentials
     * endpoint before every network git operation. */
    gitUrl: z.string(),
    defaultBranch: z.string(),
    auth: RepoAuth.default("none"),
  }),
  worktree: z.object({
    id: z.string(),
    name: z.string(),
    branch: z.string(),
    baseBranch: z.string(),
    /** Present on a new task's first job, unless it checks out an existing
     * branch. The task was created instantly under a deterministic name
     * and `branch`; the worker asks its provider CLI for a better title and
     * branch while it fetches, creates the worktree on the generated branch,
     * and reports both on `worktree.status` "ready". */
    naming: z
      .object({
        systemPrompt: z.string().min(1).max(20_000),
        prompt: z.string().min(1).max(20_000),
        uniqueSuffix: z.string().min(1).max(64),
        issueIdentifier: z.string().max(64).optional(),
        /** The title the task was created with. Absent when that title is
         * authoritative (a Linear issue title): only the branch is generated. */
        placeholderTitle: z.string().max(200).optional(),
      })
      .optional(),
    /** `branch` already exists on the remote (a task started from a branch
     * or pull request): check it out instead of creating it off `baseBranch`.
     * Never combined with `naming`, which would rename it. */
    existingBranch: z.boolean().optional(),
    /** Git-native backup handed off from a source worktree, fetched + reset
     * once after provisioning to reproduce that worktree's working state. */
    handoff: z
      .object({
        sourceWorktreeId: z.string(),
        wipCommit: z.string(),
        baseCommit: z.string(),
      })
      .optional(),
  }),
  /** Resume an earlier CLI session (steering after process exit). */
  resumeSessionId: z.string().optional(),
  /** Files attached to the prompt. The worker downloads each one into the
   * worktree's `.context` directory and references those paths in the prompt. */
  attachments: AttachmentRefs.optional(),
  /** MCP servers this runtime should be given. Absent for ordinary tasks; the
   * assistant carries exactly one (the Rivets server, with a session-scoped
   * bearer token). Additive on purpose: any job can be given tools later. */
  mcp: z.array(McpServerConfig).max(MAX_JOB_MCP_SERVERS).optional(),
  /**
   * Where the daemon runs this job. Absent means `worktree`: a git worktree of
   * `repo` on `worktree.branch`, which is what every task is.
   *
   * `scratch` is the assistant's dedicated location — a private local-only
   * directory that is not a checkout of any repository, so the daemon must not
   * fetch, clone or `git worktree add` for it (FRNT-699). `repo` is still sent
   * because the row it came from is nominal, not because anything clones it.
   */
  workspace: z.object({ kind: z.enum(["worktree", "scratch"]) }).optional(),
});
export type JobSpec = z.infer<typeof JobSpec>;

// ---------------------------------------------------------------------------
// Worker socket
// ---------------------------------------------------------------------------

/** Upper bound on agent events the daemon may coalesce into one `job.events`
 * frame. Bounds the frame size and the size of a single ChatRoom append batch;
 * the daemon flushes early on this count as well as on its time window. */
export const JOB_EVENT_BATCH_MAX = 250;
/** How long the daemon holds agent events before shipping a batch. Short
 * enough to stay imperceptible in the live view, long enough to collapse the
 * bursts an agent emits while streaming a single turn. */
export const JOB_EVENT_BATCH_MS = 50;

/** Upper bound on `subscribe.chat.maxReplay`. */
export const CHAT_REPLAY_MAX = 5_000;

/** Ceiling on `job.update.error`, in characters. The worker truncates to it
 * (a provider can dump megabytes of stderr); the server clamps whatever error
 * text it writes itself. The job row and its status event both carry it. */
export const JOB_UPDATE_ERROR_MAX = 8_000;

export const WorkerToServer = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("hello"),
    name: z.string(),
    workerVersion: z.string(),
    capabilities: WorkerCapabilities,
    /** Jobs this daemon instance still owns in memory (queued or running). */
    activeJobIds: z.array(z.string()).max(10_000),
    /** Identity of the persisted worker outbound spool for this daemon. */
    outboundDeliveryId: z.string().uuid().optional(),
  }),
  z.object({
    type: z.literal("capabilities.update"),
    capabilities: WorkerCapabilities,
  }),
  z.object({
    type: z.literal("job.update"),
    /** Monotonic, worker-local delivery sequence. Frames carrying this value
     * are retained on disk until WorkerHub cumulatively acknowledges them. */
    deliverySeq: z.number().int().positive().optional(),
    /** Persisted spool identity paired with deliverySeq. */
    deliveryId: z.string().uuid().optional(),
    jobId: z.string(),
    chatId: z.string(),
    status: JobStatus,
    agentSessionId: z.string().optional(),
    error: z.string().max(JOB_UPDATE_ERROR_MAX).optional(),
  }),
  /** The agent started a turn on its own after its job ended — a background
   * task finished and woke it, say. Sent before any `job.events` for `jobId`,
   * which is a fresh, worker-minted job id; those events carry the parent's
   * `turnId`/`userId`, so the output reads as a continuation of that turn, and
   * a terminal `job.update` for `jobId` ends it. WorkerHub opens the job as
   * `running` (idempotent on `jobId`) while the parent is terminal and belongs
   * to this worker and chat; otherwise it refuses with `job_continue_rejected`
   * and the worker stops the continuation. */
  z.object({
    type: z.literal("job.continued"),
    deliverySeq: z.number().int().positive().optional(),
    deliveryId: z.string().uuid().optional(),
    jobId: z.string().min(1).max(160),
    parentJobId: z.string().min(1).max(160),
    chatId: z.string().min(1),
    reason: z.enum(["background_task", "agent_initiated"]),
  }),
  /** Receipt of a durable server command. This acknowledges acceptance or a
   * duplicate already accepted by this worker, never successful completion. */
  z.object({
    type: z.literal("command.ack"),
    deliverySeq: z.number().int().positive().optional(),
    deliveryId: z.string().uuid().optional(),
    commandId: z.string().min(1).max(160),
    jobId: z.string().min(1),
  }),
  /** Agent output, coalesced by the daemon over a short window. Always plural:
   * every event costs a ChatRoom append and an OrgHub fan-out round-trip, so
   * one frame per event is the dominant per-job backend cost. The daemon
   * batches; the server must never un-batch when it appends. */
  z.object({
    type: z.literal("job.events"),
    deliverySeq: z.number().int().positive().optional(),
    deliveryId: z.string().uuid().optional(),
    jobId: z.string(),
    chatId: z.string(),
    events: z.array(z.object({
      turnId: z.string().min(1),
      userId: z.string().min(1),
      event: AgentEvent,
      /** Provider-native id, when exposed. Never used for attribution. */
      providerTurnId: z.string().optional(),
    })).min(1).max(JOB_EVENT_BATCH_MAX),
  }),
  z.object({
    type: z.literal("snapshot.updated"),
    deliverySeq: z.number().int().positive().optional(),
    deliveryId: z.string().uuid().optional(),
    worktreeId: z.string(),
    snapshotId: z.string(),
  }),
  z.object({
    type: z.literal("worktree.status"),
    deliverySeq: z.number().int().positive().optional(),
    deliveryId: z.string().uuid().optional(),
    worktreeId: z.string(),
    status: z.enum(["provisioning", "ready", "error"]),
    error: z.string().optional(),
    /** What the provider named the task (`JobSpec.worktree.naming`), sent
     * with "ready". `branch` is the branch the worktree was created on;
     * `title` replaces the task's name only while it is still
     * `replacesTitle`, so a rename the user made in the meantime wins. */
    identity: z
      .object({
        branch: z.string().min(1).max(200).optional(),
        title: z.string().min(1).max(200).optional(),
        replacesTitle: z.string().max(200).optional(),
      })
      .optional(),
  }),
  /** Response to a `file.fetch` request. */
  z.object({
    type: z.literal("file.content"),
    reqId: z.string(),
    worktreeId: z.string(),
    path: z.string(),
    content: z.string().optional(),
    binary: z.boolean().default(false),
    /** Set for a previewable image, whose `content` is then base64. */
    mediaType: z.string().optional(),
    error: z.string().optional(),
  }),
  /** Response to a `file.write` request. `hash` is the SHA-256 of the file as
   * written (`fileContentHash`). `code` is `conflict` when the file no longer
   * matched the `expectedHash` the edit began from — nothing was written. */
  z.object({
    type: z.literal("file.write.result"),
    reqId: z.string(),
    worktreeId: z.string(),
    path: z.string(),
    hash: z.string().optional(),
    error: z.string().optional(),
    code: z.string().optional(),
  }),
  /** Response to a `dir.list` request: one directory's immediate children.
   * The file browser asks for a directory as it is opened, so nothing walks
   * the whole worktree and an ignored directory costs nothing until someone
   * looks inside it. */
  z.object({
    type: z.literal("dir.entries"),
    reqId: z.string(),
    worktreeId: z.string(),
    path: z.string(),
    entries: z.array(DirectoryEntry).max(MAX_DIRECTORY_ENTRIES).default([]),
    /** The directory held more children than one response may carry. */
    truncated: z.boolean().default(false),
    error: z.string().optional(),
  }),
  z.object({
    type: z.literal("pong"),
    t: z.number(),
    /** Current daemon-owned queue/runtimes, used to detect lost accepted work. */
    activeJobIds: z.array(z.string()).max(10_000),
  }),
  /** The repo's script set for a worktree, sent once the worker has read
   * `.rivets/settings.toml`, and again whenever a run changes state.
   *
   * Deliberately live-only rather than spooled: it is a whole-state snapshot,
   * so the newest send makes every earlier one redundant, and the worker
   * republishes what it still owns on every `hello`. Spooling it would grow
   * the durable queue with frames whose only reader wants the latest one. */
  z.object({
    type: z.literal("script.state"),
    worktreeId: z.string(),
    scripts: WorktreeScriptSet.shape.scripts,
    problems: WorktreeScriptSet.shape.problems,
  }),
  /** The three git results below are spooled (`deliverySeq`/`deliveryId`),
   * so one whose request outlived its socket still arrives. A result nobody is
   * waiting for any more makes WorkerHub fan out the worktree's git state, so
   * clients refetch what actually happened. */
  GitWorkspaceResult.extend({
    type: z.literal("git.workspace.result"),
    deliverySeq: z.number().int().positive().optional(),
    deliveryId: z.string().uuid().optional(),
    reqId: z.string(),
    worktreeId: z.string(),
  }),
  /** The task's name and branch, written by the user's own provider CLI. Like
   * `git.copy.result` it never fails the caller: a provider that is missing or
   * answers with nothing usable simply reports `generated: false`, and the
   * server names the task deterministically. */
  z.object({
    type: z.literal("task.identity.result"),
    reqId: z.string(),
    title: z.string().optional(),
    branch: z.string().optional(),
    generated: z.boolean(),
  }),
  z.object({
    type: z.literal("git.discard.result"),
    deliverySeq: z.number().int().positive().optional(),
    deliveryId: z.string().uuid().optional(),
    reqId: z.string(),
    worktreeId: z.string(),
    discarded: z.number().int().nonnegative().optional(),
    error: z.string().optional(),
    code: z.string().optional(),
  }),
  /** Always carries usable copy: a provider that fails or answers with nothing
   * usable falls back to a deterministic subject on the worker, so there is no
   * error arm here for the caller to have to handle. */
  z.object({
    type: z.literal("git.copy.result"),
    deliverySeq: z.number().int().positive().optional(),
    deliveryId: z.string().uuid().optional(),
    model: z.string().optional(), generationError: z.string().optional(),
    templates: z.array(z.string()).optional(),
    reqId: z.string(),
    worktreeId: z.string(),
    message: z.string().optional(),
    title: z.string().optional(),
    body: z.string().optional(),
    /** True when the provider wrote it; false when the fallback did. */
    generated: z.boolean(),
  }),
]);
export type WorkerToServer = z.infer<typeof WorkerToServer>;

export const ServerToWorker = z.discriminatedUnion("type", [
  ProtocolReady,
  ProtocolIncompatible,
  z.object({ type: z.literal("hello.ok"), workerId: z.string() }),
  /** Cumulative acknowledgement for the worker's persisted outbound spool.
   * WorkerHub emits it only after the corresponding frame's durable handling
   * has completed. */
  z.object({ type: z.literal("worker.delivery.ack"), deliveryId: z.string().uuid(), deliverySeq: z.number().int().nonnegative() }),
  z.object({ type: z.literal("job.assign"), job: JobSpec, commandId: z.string().min(1).max(160) }),
  z.object({
    type: z.literal("job.steer"),
    commandId: z.string().min(1).max(160),
    jobId: z.string(),
    chatId: z.string(),
    text: z.string(),
    turn: TurnActor,
    attachments: AttachmentRefs.optional(),
    /** "Send now": also move this steer to the front and interrupt the
     * current turn, atomically with accepting it. The one command for a new
     * message sent now; `job.steer.prioritize` remains only for promoting a
     * steer that is already queued (`POST …/turns/:turnId/send-now`). */
    prioritize: z.literal(true).optional(),
  }),
  /** Rewrites the text of a steer the worker has already accepted but whose
   * turn the provider has not started yet. Delivered as its own durable
   * command because the steer it edits may already be sitting in the
   * provider's queue; the worker replies with the ordinary command ack and
   * reports an unappliable edit as an error log on the chat. */
  z.object({
    type: z.literal("job.steer.revise"),
    commandId: z.string().min(1).max(160),
    jobId: z.string(),
    chatId: z.string(),
    text: z.string(),
    turn: TurnActor,
  }),
  /** Removes a queued steer before the provider takes it. Same ordering and
   * reporting rules as `job.steer.revise`. */
  z.object({
    type: z.literal("job.steer.withdraw"),
    commandId: z.string().min(1).max(160),
    jobId: z.string(),
    chatId: z.string(),
    turn: TurnActor,
  }),
  /** Moves a queued steer to the front of the provider's queue and interrupts
   * the turn in progress so it starts now. The run itself continues. */
  z.object({
    type: z.literal("job.steer.prioritize"),
    commandId: z.string().min(1).max(160),
    jobId: z.string(),
    chatId: z.string(),
    turn: TurnActor,
  }),
  z.object({ type: z.literal("job.cancel"), jobId: z.string(), commandId: z.string().min(1).max(160) }),
  z.object({
    type: z.literal("file.fetch"),
    reqId: z.string(),
    worktreeId: z.string(),
    path: z.string(),
  }),
  /** Replace an existing text file's content in the worktree — the file
   * editor's save. Only a file that already exists is written. With
   * `expectedHash` set, the worker writes only while the file still hashes to
   * it, so a save can never silently clobber what an agent wrote meanwhile.
   * `branch`/`baseBranch` let the worker publish the snapshot that puts the
   * edit in the Changes list. Sent only to workers advertising `fileWrite`. */
  z.object({
    type: z.literal("file.write"),
    reqId: z.string(),
    worktreeId: z.string(),
    branch: z.string(),
    baseBranch: z.string(),
    path: z.string().min(1).max(4_096),
    content: z.string(),
    expectedHash: z.string().regex(/^[0-9a-f]{64}$/).optional(),
  }),
  z.object({
    type: z.literal("dir.list"),
    reqId: z.string(),
    worktreeId: z.string(),
    /** Empty means the worktree root. */
    path: z.string(),
  }),
  /** Start a `run` script, or re-run `setup`, on a worktree this worker owns.
   * `gitIdentity` matches the one OrgHub attaches to a `terminal.create`: the
   * script gets the same git environment an interactive shell in that
   * worktree would. */
  z.object({
    type: z.literal("script.run"),
    worktreeId: z.string(),
    scriptId: z.string().min(1).max(48),
    gitIdentity: TurnActor.shape.gitIdentity,
  }),
  z.object({
    type: z.literal("script.stop"),
    worktreeId: z.string(),
    scriptId: z.string().min(1).max(48),
  }),
  /** Publish this worktree's current script set again.
   *
   * `script.state` is live-only, so a client that connected after the last one
   * went out has nothing. Rather than persisting a snapshot server-side — which
   * would then have to be invalidated and reconciled — the client asks, and the
   * worker, which is the authority, answers. It also lets a daemon that
   * restarted read a worktree it never provisioned. */
  z.object({
    type: z.literal("script.refresh"),
    worktreeId: z.string(),
  }),
  z.object({
    type: z.literal("git.workspace"), reqId: z.string(), worktreeId: z.string(),
    repo: JobSpec.shape.repo, branch: z.string(), baseBranch: z.string(),
    action: GitWorkspaceAction, operationId: z.string().min(1).max(100),
    preparationId: z.string().optional(), message: z.string().max(500).optional(),
    paths: z.array(z.string().min(1)).optional(), authorName: z.string(), authorEmail: z.string().email(),
    refresh: z.boolean().optional(),
  }),
  /** Throw away uncommitted changes to `paths` in the checkout: tracked files
   * go back to HEAD, untracked ones are removed. Never touches commits. */
  z.object({
    type: z.literal("git.discard"),
    reqId: z.string(),
    worktreeId: z.string(),
    repo: JobSpec.shape.repo,
    branch: z.string(),
    baseBranch: z.string(),
    paths: z.array(z.string().min(1)).min(1).max(200),
  }),
  /** Ask the worker to name a task — its title and a Git branch — from the
   * prompt, before the worktree exists. It runs on the user's own provider
   * credentials, so no server-side model is ever billed for it. `orgId` lets
   * the WorkerHub confirm the request is for the organization it serves, since
   * there is no worktree to check ownership against yet. */
  z.object({
    type: z.literal("task.identity"),
    reqId: z.string(),
    orgId: z.string(),
    providerId: ProviderId,
    systemPrompt: z.string().min(1).max(20_000),
    prompt: z.string().min(1).max(20_000),
  }),
  /** Ask the worker to write a commit subject or pull-request copy for the
   * change in its own checkout. The worker holds the diff and the user's own
   * provider credentials, so the text is written there and only the resolved
   * prompt travels out. */
  z.object({
    type: z.literal("git.copy"),
    model: z.string().min(1).max(200).optional(), inferCommitConvention: z.boolean().optional(),
    reqId: z.string(),
    worktreeId: z.string(),
    branch: z.string(),
    baseBranch: z.string(),
    kind: GitCopyKind,
    scope: GitCopyScope,
    /** The provider whose CLI writes the copy, so the cost lands on the
     * credentials the user already runs their agents with. */
    providerId: ProviderId,
    /** Prompt resolved through the platform/org/user/repo override layers. */
    systemPrompt: z.string().min(1).max(20_000),
    /** The task's name, as background on what the user asked for. */
    taskTitle: z.string().max(200).optional(),
    /** Bounded recent task discussion, used as attributed background. */
    taskContext: z.string().max(8_000).optional(),
    preparationId: z.string().optional(),
    committedSha: z.string().optional(),
    templatePath: z.string().optional(),
    /** Set when the client is committing a subset of the changed files. */
    paths: z.array(z.string().min(1)).optional(),
  }),
  /** Someone asked to see current subscription usage. The worker re-reads
   * every ready provider and answers with an ordinary `capabilities.update`,
   * so there is no reply frame to wait for. */
  z.object({ type: z.literal("usage.refresh") }),
  /** Drop LocalApi session tokens matching these fields and close their
   * sockets with `local.revoked`. Sent on access changes and member removal
   * to workers with a running loopback descriptor (which always serves
   * LocalApi). Absent `userId`/`grantId` widen the match: org-wide, or every
   * grant of that user. Live-only: grants die with their TTL anyway. */
  z.object({
    type: z.literal("local.revoke"),
    orgId: z.string().min(1),
    userId: z.string().min(1).optional(),
    grantId: z.string().min(1).optional(),
  }),
  z.object({ type: z.literal("ping"), t: z.number() }),
  z.object({
    type: z.literal("error"),
    code: z.string(),
    message: z.string(),
    /** Set on outbound delivery errors (see `OutboundDeliveryErrorCode`). */
    deliverySeq: z.number().int().positive().optional(),
    /** Set on a job refusal (`job_finished`, `job_status_invalid`,
     * `job_continue_rejected`, `job_unknown`) only when the job is over as far
     * as the server is concerned, or was never its: the worker stops it. */
    jobId: z.string().optional(),
  }),
]);
export type ServerToWorker = z.infer<typeof ServerToWorker>;

/** Codes WorkerHub puts in `error` frames about the worker's outbound spool. */
export const OutboundDeliveryErrorCode = {
  /** A later frame arrived first; `deliverySeq` is the one WorkerHub expects. */
  gap: "outbound_delivery_gap",
  /** Applying frame `deliverySeq` failed. It is not ACKed; replay it. */
  frameFailed: "outbound_frame_failed",
  /** WorkerHub gave up on frame `deliverySeq` and ACKed it without applying it. */
  deadLettered: "outbound_frame_dead_lettered",
} as const;

// ---------------------------------------------------------------------------
// Client socket
// ---------------------------------------------------------------------------

export const ClientToServer = z.discriminatedUnion("type", [
  /** Subscribe to a chat's event stream; replays events with seq > afterSeq. */
  z.object({
    type: z.literal("subscribe.chat"),
    chatId: z.string(),
    afterSeq: z.number().default(0),
    /** Replay at most this many of the newest missing events. When the gap is
     * larger, the first replay frame carries `hasGapBefore: true` and
     * `coverage.fromSeq` at the first replayed event; older history is paged
     * with `?beforeSeq=`. The replay always elides superseded deltas
     * (`elideSupersededDeltas`) and every replay frame carries `coverage`. */
    maxReplay: z.number().int().min(1).max(CHAT_REPLAY_MAX),
  }),
  z.object({ type: z.literal("unsubscribe.chat"), chatId: z.string() }),
  z.object({ type: z.literal("typing"), chatId: z.string() }),
  /** Always the constant `{"type":"ping","t":0}`, so OrgHub can answer it
   * with `setWebSocketAutoResponse` without waking. */
  z.object({ type: z.literal("ping"), t: z.literal(0) }),
  z.object({
    type: z.literal("script.run"),
    worktreeId: z.string(),
    scriptId: z.string().min(1).max(48),
  }),
  z.object({
    type: z.literal("script.stop"),
    worktreeId: z.string(),
    scriptId: z.string().min(1).max(48),
  }),
  z.object({
    type: z.literal("script.refresh"),
    worktreeId: z.string(),
  }),
]);
export type ClientToServer = z.infer<typeof ClientToServer>;

export const OrgUpdate = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("worker.updated"), worker: WorkerInfo }),
  z.object({ kind: z.literal("worker.removed"), workerId: z.string() }),
  z.object({
    kind: z.literal("worker.status"),
    workerId: z.string(),
    status: WorkerStatus,
    capabilities: WorkerCapabilities.optional(),
  }),
  z.object({ kind: z.literal("repo.updated"), repo: Repo }),
  z.object({ kind: z.literal("org.detail.changed") }),
  z.object({ kind: z.literal("github.updated") }),
  z.object({ kind: z.literal("access.changed") }),
  z.object({ kind: z.literal("access.version"), revision: z.number().int().nonnegative() }),
  z.object({ kind: z.literal("org.scope.hidden") }),
  z.object({ kind: z.literal("chat.updated"), chat: Chat }),
  z.object({ kind: z.literal("chat.participants"), chatId: z.string(), worktreeId: z.string(), participantIds: z.array(z.string()) }),
  z.object({ kind: z.literal("worktree.updated"), worktree: Worktree }),
  z.object({ kind: z.literal("worktree.removed"), worktreeId: z.string(), repoId: z.string() }),
  z.object({ kind: z.literal("job.updated"), job: Job }),
  z.object({
    kind: z.literal("snapshot.updated"),
    worktreeId: z.string(),
    snapshotId: z.string(),
    /** The worktree's new `diffStat` (`diffStatFromSummary`). */
    diffStat: DiffStat,
  }),
  /** A git operation finished that nobody was waiting for any more — its
   * request timed out, or the worker lost contact mid-operation (P3). The
   * worktree's git state may have changed: clients refetch it rather than
   * trusting what they showed when the request gave up. */
  z.object({ kind: z.literal("worktree.git.changed"), worktreeId: z.string() }),
  z.object({
    kind: z.literal("pr.updated"),
    worktreeId: z.string(),
    pullRequest: PullRequestInfo,
    /** Authoritative GitHub state when this update was triggered by a status
     * refresh or webhook; absent otherwise, and clients then fetch it. */
    status: PullRequestStatus.optional(),
  }),
]);
export type OrgUpdate = z.infer<typeof OrgUpdate>;

export const PresenceUser = z.object({
  userId: z.string(),
  name: z.string(),
  typing: z.boolean().default(false),
});
export type PresenceUser = z.infer<typeof PresenceUser>;

export const ServerToClient = z.discriminatedUnion("type", [
  ProtocolReady,
  ProtocolIncompatible,
  /** Batched replay and live events share one frame shape (live = length 1). */
  z.object({
    type: z.literal("chat.events"),
    chatId: z.string(),
    events: z.array(ChatEvent),
    /** On every replay frame: the seq interval it scanned, including elided
     * and absent seqs. Advance the contiguity cursor to `coverage.throughSeq`.
     * Never on live frames, which elide nothing and prove no contiguity. */
    coverage: ChatEventsCoverage.optional(),
    /** First replay frame of a `maxReplay`-bounded subscription whose gap was
     * larger than `maxReplay`: history before `coverage.fromSeq` was not sent
     * and is not loaded. */
    hasGapBefore: z.boolean().optional(),
  }),
  z.object({
    type: z.literal("presence"),
    chatId: z.string(),
    users: z.array(PresenceUser),
  }),
  z.object({ type: z.literal("org.update"), revision: z.number().int().positive(), update: OrgUpdate }),
  /**
   * An `OrgUpdate` that belongs to exactly one member — today, the chats and
   * jobs inside their private assistant workspace, whose titles are the
   * member's own prompts (FRNT-699).
   *
   * Deliberately outside the `org.update` revision stream. That sequence is
   * how every other socket in the organization detects it missed something, so
   * a frame nobody else receives must not consume a number they are expected to
   * observe — otherwise one member talking to the assistant would make every
   * colleague's client re-reconcile several times per turn. A private update is
   * applied immediately and never advances `orgRevision`.
   */
  z.object({ type: z.literal("org.update.private"), update: OrgUpdate }),
  /** The constant auto-response to the constant ping. */
  z.object({ type: z.literal("pong"), t: z.literal(0) }),
  /** Fan-out of the worker's `script.state`, so every viewer of a worktree
   * sees the same script list, states, and output terminals. */
  WorktreeScriptSet.extend({ type: z.literal("script.state") }),
  /** A `subscribe.chat` that could not be served: the access check or the
   * replay failed. The chat is not live — no presence follows — and the client
   * keeps it marked unsynced, retrying with backoff while `retryable`. */
  z.object({
    type: z.literal("chat.subscribe_failed"),
    chatId: z.string(),
    code: z.string(),
    message: z.string(),
    retryable: z.boolean(),
  }),
  z.object({
    type: z.literal("error"),
    code: z.string(),
    message: z.string(),
    /** Set when the error concerns one chat's subscription. */
    chatId: z.string().optional(),
  }),
]);
export type ServerToClient = z.infer<typeof ServerToClient>;

/** Parse an incoming frame, returning undefined (never throwing) on junk. */
export function parseFrame<T>(schema: z.ZodType<T>, raw: unknown): T | undefined {
  if (typeof raw !== "string") return undefined;
  try {
    const result = schema.safeParse(JSON.parse(raw));
    return result.success ? result.data : undefined;
  } catch {
    return undefined;
  }
}
